import { flowRight } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import Wix from '../../../app/services/wix-sdk-polyfill';
import * as appLifecycleService from '../../../app/services/app-lifecycle-service';
import { fetchProfilePublications } from '../../../app/actions/fetch-posts-by-user';
import { setIsLoaded } from '../../../app/actions/set-is-loaded';
import { incrementPostLikeCount } from '../../../app/actions/increment-post-like-count';
import AnimatedLoader from '../../../app/components/animated-loader';
import NoPosts from '../../components/no-posts';
import ProfileContentFrame from '../../../member-pages-common/components/profile-content-frame';
import { getIsProfilePostsLoading } from '../../../app/selectors/is-loading-selectors';
import { getIsProfilePostsLoaded } from '../../../app/selectors/is-loaded-selectors';
import { getEntityCount } from '../../../app/selectors/pagination-selectors';
import { getSortedPostsBySiteMemberId, getFeedPosts } from '../../../app/selectors/post-selectors';
import { getCurrentUserSiteMemberId, POSTS_PER_PAGE } from '@wix/communities-blog-client-common';
import CreatePostButton from '../../../member-pages-common/containers/create-post-button';
import InfiniteListLoader from '../../../app/components/infinite-list-loader';
import PostList from '../../components/post-list';
import Loader from '../../../app/components/loader';
import withAuth from '../../../app/hoc/with-auth';
import withTranslate from '../../../app/hoc/with-translate';
import withPermissions from '../../../app/hoc/with-permissions';
import fedopsLogger from '../../services/fedops-logger';
import { getIsDemoMode } from '../../../app/selectors/is-demo-mode-selectors';
import fetchFeedPosts from '../../../app/actions/fetch-feed-posts';
import { BI_PAGE_MEMBERS_POSTS } from '../../../member-pages-common/constants/bi-pages';

const reset = () => ({ page: 1 });
const increment = (state) => ({ page: state.page + 1 });

class ProfileBlogPosts extends Component {
  state = { page: 1 };

  componentDidMount = () => {
    this.fetchUserDataWithCurrentUser().then(appLifecycleService.appLoaded);
    Wix.addEventListener(Wix.Events.PAGE_NAVIGATION_IN, this.fetchUserDataWithCurrentUser);
    fedopsLogger.appLoaded();
  };

  componentWillReceiveProps = ({ siteMemberId: nextSiteMemberId }) => {
    const { siteMemberId } = this.props;
    if (siteMemberId !== nextSiteMemberId) {
      this.setState(reset, () => this.fetchUserData(nextSiteMemberId));
    }
  };

  componentWillUnmount = () => {
    this.props.setIsLoaded('profile-posts', '', false);
    Wix.removeEventListener(Wix.Events.PAGE_NAVIGATION_IN, this.fetchUserDataWithCurrentUser);
  };

  fetchUserDataWithCurrentUser = () => {
    return this.fetchUserData(this.props.siteMemberId);
  };

  fetchUserData = (siteMemberId) => {
    this.props.setIsLoaded('profile-posts', '', false);
    return this.props.isDemoMode ? this.props.fetchFeedPosts() : this.fetchPosts(siteMemberId);
  };

  fetchPosts = (siteMemberId = this.props.siteMemberId) => {
    const { page } = this.state;
    return this.props.fetchPosts({
      siteMemberId,
      page,
      pageSize: POSTS_PER_PAGE,
    });
  };

  handleLoad = () => this.setState(increment, this.fetchPosts);

  renderNoPosts = () => {
    const { can, isAtOwnProfile } = this.props;
    return <NoPosts isAllowedToCreatePost={(isAtOwnProfile && can('create', 'post')) || !isAtOwnProfile} />;
  };

  renderPostList = () => {
    const { incrementPostLikeCount, posts, postsCount, isLoading, isLoaded, isAtOwnProfile, can } = this.props;
    return (
      <div>
        <PostList
          posts={posts}
          onLikeClick={incrementPostLikeCount}
          isOwner={isAtOwnProfile && can('create', 'post')}
        />
        {isLoaded && (
          <InfiniteListLoader
            onLoad={this.handleLoad}
            loader={<Loader />}
            isLoading={isLoading}
            hasMore={posts.length < postsCount}
          />
        )}
      </div>
    );
  };

  render = () => {
    const { isLoading, isLoaded, posts, t } = this.props;

    return (
      <ProfileContentFrame
        title={t('profile-blog-posts.blog-posts')}
        actions={<CreatePostButton biPage={BI_PAGE_MEMBERS_POSTS} />}
      >
        <AnimatedLoader isLoading={isLoading && !posts.length}>
          {!posts.length && isLoaded ? this.renderNoPosts() : this.renderPostList()}
        </AnimatedLoader>
      </ProfileContentFrame>
    );
  };
}

ProfileBlogPosts.propTypes = {
  siteMemberId: PropTypes.string.isRequired,
  fetchPosts: PropTypes.func,
  setIsLoaded: PropTypes.func,
  incrementPostLikeCount: PropTypes.func,
  posts: PropTypes.array,
  postsCount: PropTypes.number,
  isLoading: PropTypes.bool,
  isLoaded: PropTypes.bool,
  isAtOwnProfile: PropTypes.bool,
  isDemoMode: PropTypes.bool,
  t: PropTypes.func,
  can: PropTypes.func,
  fetchFeedPosts: PropTypes.func,
};

ProfileBlogPosts.defaultProps = {
  posts: [],
  page: 1,
};

const mapStateToProps = (state, { siteMemberId }) => {
  const isDemoMode = getIsDemoMode(state);

  return {
    isAtOwnProfile: getCurrentUserSiteMemberId(state) === siteMemberId,
    posts: isDemoMode ? getFeedPosts(state) : getSortedPostsBySiteMemberId(state, siteMemberId),
    postsCount: getEntityCount(state, 'posts'),
    isLoading: getIsProfilePostsLoading(state),
    isLoaded: getIsProfilePostsLoaded(state),
    isDemoMode,
  };
};

const mapDispatchToProps = {
  fetchPosts: fetchProfilePublications,
  incrementPostLikeCount,
  setIsLoaded,
  fetchFeedPosts,
};

// prettier-ignore
export default flowRight(
  withAuth,
  connect(mapStateToProps, mapDispatchToProps),
  withTranslate,
  withPermissions,
)(ProfileBlogPosts);
